import axios, {AxiosResponse} from 'axios';
import {computed, watch} from 'vue';
import {useStore} from 'vuex';
import {formatDateTimeForQuery} from "@utilities/helpers";
import {KPIReportType, STATReportType, SubjectDataType} from "@utilities/reports/types";

export function useReporting(type: "kpi" | "stat") {
    const store = useStore();
    const baseURL: string = localized.baseURL;

    const selectedShop = computed((): number => store.state.selectedShop);
    const techId = computed((): number => store.state.techId);
    const dates = computed((): Array<Date> => store.state.dates);

    // <editor-fold desc="Report Fetching Functions">----------------------------------------

    // Function to get the KPI report
    const getKPIReport = (): void => {

        // Prepare the parameters for the API request
        const params = {
            // Ensure start_date and end_date are in the correct format for the query
            start: formatDateTimeForQuery(dates.value[0] ?? new Date()),
            end: formatDateTimeForQuery(dates.value.at(-1) ?? new Date(dates[0])),
            shop_id: selectedShop.value,
            tech_id: techId.value
        }

        // Make the API call to fetch the KPI report
        axios.get(`${baseURL}reports/kpi`, {params})
            .then((response: AxiosResponse<Array<any>>) => {
                console.log("KPI Report successfully fetched: ", response.data);
                store.commit('setKPIReport', response.data);
            }).catch(error => {
            console.error(error);
        });
    };

    // Function to get the STAT report
    const getSTATReport = () => {
        // Prepare the parameters for the API request
        const params = {
            // Ensure start_date and end_date are in the correct format for the query
            start: formatDateTimeForQuery(dates.value[0] ?? new Date()),
            end: formatDateTimeForQuery(dates.value.at(-1) ?? new Date(dates[0])),
            shop_id: selectedShop.value,
            tech_id: techId.value
        }

        // Make the API call to fetch the KPI report
        axios.get(`${baseURL}reports/stat`, {params})
            .then((response: AxiosResponse<Array<any>>) => {
                console.log("STAT Report successfully fetched: ", response.data);
                store.commit('setSTATReport', response.data);
            }).catch(error => {
            console.error(error);
        });
    }

    const reportData = computed((): KPIReportType | STATReportType => {
        if (type === 'kpi') {
            return store.state.kpiReport as KPIReportType;
        } else if (type === 'stat') {
            return store.state.statReport as STATReportType;
        }
        return {} as KPIReportType | STATReportType;
    });

    const getReport = (): void => {
        if (!type) {
            console.error("Report type is not defined.");
        }
        if (type === 'kpi') {
            getKPIReport();
        }
        if (type === 'stat') {
            getSTATReport();
        }
    }

    // </editor-fold>---------------------------------------------------------------

    // <editor-fold desc="Report Data Functions">----------------------------------------

    // Get the number of days in the month
    const daysInMonth = computed(() => {
        const firstDate = new Date(dates.value?.[0] ?? 0);
        return firstDate ? new Date(firstDate.getFullYear(), firstDate.getMonth() + 1, 0).getDate() : 0;
    });

    // Check if a day has data with the computed function value
    const hasDataForDay = (day: number): boolean => {
        if (!reportData?.value?.data?.totalSales) return false;

        // Adjust day to 0-based index for the data array
        const dataIndex = day - 1;
        return day - 1 >= 0 && dataIndex < reportData.value.data.totalSales.daily.length;
    };

    // Get the value for a specific day and row
    const getValueForDay = (row: SubjectDataType<any>, day: number) => {
        if (!hasDataForDay(day)) return '';

        // Adjust day to 0-based index for the data array
        const dataIndex = day - 1;
        return row.daily[dataIndex];
    };

    // Check if EOD was submitted for a specific day
    const isEodSubmitted = (day: number) => {
        if (!hasDataForDay(day)) return true; // Default to true for days without data

        // Adjust day to 0-based index for the data array
        const dataIndex = day - 1;

        // Check if eodSubmitted data exists and is true for this day
        return reportData.value?.data?.eodSubmitted?.daily[dataIndex] === true;
    };

    // Check if the shop was open on a specific day
    const isOpen = (day: number) => {
        if (!hasDataForDay(day)) return false; // Default to false for days without data

        // Adjust day to 0-based index for the data array
        const dataIndex = day - 1;

        // Check if open data exists and is true for this day
        return reportData.value?.data?.open?.daily[dataIndex] === true;
    };
    
    // </editor-fold>-------------------------------------------------------------

    // <editor-fold desc="Watchers">-------------------------------------------------

    watch(dates, () => {
        getReport();
    }, {immediate: true});

    watch(selectedShop, () => {
        getReport();
    });

    watch(techId, () => {
        getReport();
    });

    // </editor-fold>---------------------------------------------------------------

    return {
        selectedShop,
        techId,
        dates,
        daysInMonth,
        hasDataForDay,
        isEodSubmitted,
        isOpen,
        getValueForDay,
        reportData,
        getReport,
        getKPIReport
    };
}